// NOTE: The Time Period report has different breakdown inputs from the generic BreakdownDrawer,
// so it gets its own component to reduce the use of conditionals in the generic component.
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dropdown,
  MultipleDropdown,
  removeObjectByValue,
  Toggle,
  toggleObject,
  Tooltip,
} from '@makeably/creativex-design-system';
import ChartMetricSelection from 'components/reporting/ChartMetricSelection';
import ReportingDrawer from 'components/reporting/ReportingDrawer';
import {
  isSameByValue,
} from 'utilities/array';
import { track } from 'utilities/mixpanel';
import { pluralizeByCount } from 'utilities/string';
import styles from './TimePeriodDrawer.module.css';

const optionProps = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
});
const optionPropsArray = PropTypes.arrayOf(optionProps);

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  lineCounts: optionPropsArray.isRequired,
  metrics: optionPropsArray.isRequired,
  onClose: PropTypes.func.isRequired,
  segments: optionPropsArray.isRequired,
  selectedChartMetrics: optionPropsArray.isRequired,
  selectedLineCount: optionProps.isRequired,
  selectedMetrics: optionPropsArray.isRequired,
  selectedSegments: optionPropsArray.isRequired,
  selectedShowTotals: PropTypes.bool.isRequired,
  selectedTimePeriod: optionProps.isRequired,
  selectedVizType: optionProps.isRequired,
  setSelectedChartMetrics: PropTypes.func.isRequired,
  setSelectedLineCount: PropTypes.func.isRequired,
  setSelectedMetrics: PropTypes.func.isRequired,
  setSelectedSegments: PropTypes.func.isRequired,
  setSelectedShowTotals: PropTypes.func.isRequired,
  setSelectedTimePeriod: PropTypes.func.isRequired,
  setSelectedVizType: PropTypes.func.isRequired,
  timePeriods: optionPropsArray.isRequired,
  vizTypes: optionPropsArray.isRequired,
};

const maxSegments = 1;

function generateSegmentOptions(segments, selected) {
  if (selected.length < maxSegments) {
    return segments;
  }

  return segments.map((dim) => {
    if (selected.findIndex((sel) => sel.value === dim.value) === -1) {
      return {
        ...dim,
        disabled: true,
      };
    }
    return dim;
  });
}

function updateMaxChartMetrics(vizType) {
  if (vizType?.value === 'multi_line') return 2;

  return 1;
}

function TimePeriodDrawer({
  isOpen,
  lineCounts,
  metrics,
  onClose,
  segments,
  selectedChartMetrics,
  selectedLineCount,
  selectedMetrics,
  selectedSegments,
  selectedShowTotals,
  selectedTimePeriod,
  selectedVizType,
  setSelectedChartMetrics,
  setSelectedLineCount,
  setSelectedMetrics,
  setSelectedSegments,
  setSelectedShowTotals,
  setSelectedTimePeriod,
  setSelectedVizType,
  timePeriods,
  vizTypes,
}) {
  const [segmentOptions, setSegmentOptions] = useState(
    generateSegmentOptions(segments, selectedSegments),
  );
  const [maxChartMetrics, setMaxChartMetrics] = useState();
  const [currentChartMetrics, setCurrentChartMetrics] = useState(selectedChartMetrics);
  const [currentLineCount, setCurrentLineCount] = useState(selectedLineCount);
  const [currentMetrics, setCurrentMetrics] = useState(selectedMetrics);
  const [currentSegments, setCurrentSegments] = useState(selectedSegments);
  const [currentShowTotals, setCurrentShowTotals] = useState(selectedShowTotals);
  const [currentTimePeriod, setCurrentTimePeriod] = useState(selectedTimePeriod);
  const [currentVizType, setCurrentVizType] = useState(selectedVizType);

  const isApplyDisabled = (isSameByValue(currentSegments, selectedSegments)
    && isSameByValue(currentMetrics, selectedMetrics)
    && currentTimePeriod?.value === selectedTimePeriod?.value
    && currentVizType?.value === selectedVizType?.value
    && isSameByValue(currentChartMetrics || [], selectedChartMetrics || [])
    && currentLineCount?.value === selectedLineCount?.value
    && currentShowTotals === selectedShowTotals
  );

  const isLineCountVisible = currentVizType?.value === 'single_line';

  useEffect(() => {
    setCurrentChartMetrics(selectedChartMetrics);
  }, [selectedChartMetrics]);

  useEffect(() => {
    setCurrentLineCount(selectedLineCount);
  }, [selectedLineCount]);

  useEffect(() => {
    setSegmentOptions(generateSegmentOptions(segments, selectedSegments));
    setCurrentSegments(selectedSegments);
  }, [selectedSegments]);

  useEffect(() => {
    setCurrentMetrics(selectedMetrics);
  }, [selectedMetrics]);

  useEffect(() => {
    setCurrentShowTotals(selectedShowTotals);
  }, [selectedShowTotals]);

  useEffect(() => {
    setCurrentTimePeriod(selectedTimePeriod);
  }, [selectedTimePeriod]);

  useEffect(() => {
    setCurrentVizType(selectedVizType);
  }, [selectedVizType]);

  useEffect(() => {
    setMaxChartMetrics(updateMaxChartMetrics(currentVizType));

    if (currentVizType?.value === 'multi_line') {
      setCurrentSegments([]);
      setSegmentOptions(generateSegmentOptions(segments, []));
    } else if (currentVizType?.value !== 'multi_line' && currentChartMetrics.length > 1) {
      setCurrentChartMetrics(currentChartMetrics.slice(0, 1));
    }
  }, [currentVizType]);

  useEffect(() => {
    if (!currentChartMetrics) return;

    setCurrentChartMetrics((prev) => prev.filter((metric) => currentMetrics.includes(metric)));
  }, [currentMetrics]);

  const removeMetric = (option) => {
    setCurrentMetrics((current) => removeObjectByValue(current, option));
  };

  const handleSegmentChange = (selected) => {
    const current = toggleObject(currentSegments, selected);
    setSegmentOptions(generateSegmentOptions(segments, current));
    setCurrentSegments(current);
  };

  const handleSegmentClear = () => {
    const current = [];
    setSegmentOptions(generateSegmentOptions(segments, current, maxSegments));
    setCurrentSegments(current);
  };

  const handleMetricChange = (selected) => {
    setCurrentMetrics(toggleObject(currentMetrics, selected));
  };

  const handleTimePeriodChange = (selected) => {
    setCurrentTimePeriod(selected);
    if (selected !== currentTimePeriod) {
      track('change_time_period_in_setup', { timePeriod: selected.value });
    }
  };

  const handleClose = () => {
    setSegmentOptions(generateSegmentOptions(segments, selectedSegments));
    setCurrentChartMetrics(selectedChartMetrics);
    setCurrentLineCount(selectedLineCount);
    setCurrentSegments(selectedSegments);
    setCurrentMetrics(selectedMetrics);
    setCurrentShowTotals(selectedShowTotals);
    setCurrentTimePeriod(selectedTimePeriod);
    setCurrentVizType(selectedVizType);
    onClose();
  };

  const nonChartMetrics = useMemo(() => (
    currentMetrics.filter((metric) => !currentChartMetrics?.includes(metric))
  ), [currentMetrics, currentChartMetrics]);

  const handleApply = () => {
    setSegmentOptions(generateSegmentOptions(segments, currentSegments));
    setSelectedSegments(currentSegments);
    setSelectedMetrics(currentChartMetrics.concat(nonChartMetrics));
    setSelectedChartMetrics(currentChartMetrics);
    setSelectedLineCount(currentLineCount);
    setSelectedShowTotals(currentShowTotals);
    setSelectedTimePeriod(currentTimePeriod);
    setSelectedVizType(currentVizType);

    track('apply_setup_changes', {
      chartMetrics: currentChartMetrics,
      lineCount: currentLineCount?.value,
      metrics: currentMetrics,
      segments: currentSegments,
      showTotals: currentShowTotals,
      timePeriod: currentTimePeriod?.value,
      vizType: currentVizType?.value,
    });
    onClose();
  };

  return (
    <ReportingDrawer
      header="Select Report Details"
      isApplyDisabled={isApplyDisabled}
      isOpen={isOpen}
      subheader="Choose what metrics you want to see and how you want to see them visualized."
      onApply={handleApply}
      onClose={handleClose}
    >
      <Dropdown
        label="Visualization Type"
        menuProps={{ size: 'large' }}
        options={vizTypes}
        selected={currentVizType}
        size="large"
        onChange={(type) => setCurrentVizType(type)}
      />
      <Dropdown
        label="Time Period"
        menuProps={{ size: 'large' }}
        options={timePeriods}
        selected={currentTimePeriod}
        size="large"
        onChange={handleTimePeriodChange}
      />
      { currentVizType?.value !== 'multi_line' && (
        <div className="u-flexRow u-alignEnd u-gap-16">
          <Dropdown
            label="Group by"
            menuProps={{ size: 'large' }}
            options={segmentOptions}
            selected={currentSegments[0]}
            size="large"
            onChange={handleSegmentChange}
          />
          <Button
            disabled={currentSegments.length === 0}
            label="Clear"
            variant="tertiary"
            onClick={handleSegmentClear}
          />
        </div>
      ) }
      { isLineCountVisible && (
        <Dropdown
          label="Number of Lines Showing"
          menuProps={{ size: 'large' }}
          options={lineCounts}
          selected={currentLineCount}
          size="large"
          onChange={(num) => setCurrentLineCount(num)}
        />
      ) }
      <div className="u-flexRow u-alignEnd u-gap-16">
        <MultipleDropdown
          label="Metrics"
          menuProps={{ size: 'large' }}
          options={metrics}
          selected={currentMetrics}
          size="large"
          onChange={handleMetricChange}
        />
        <Button
          disabled={currentMetrics.length === 0}
          label="Clear"
          variant="tertiary"
          onClick={() => setCurrentMetrics([])}
        />
      </div>
      <div className={styles.toggle}>
        <Toggle
          checked={currentShowTotals}
          label="Show Totals"
          right
          onChange={() => setCurrentShowTotals(!currentShowTotals)}
        />
        <Tooltip
          label="Display totals for additive metrics (e.g. posts, assets, spend)"
        />
      </div>
      <ChartMetricSelection
        chartMetrics={currentChartMetrics}
        label={`Select ${maxChartMetrics} ${pluralizeByCount('Metric', maxChartMetrics)} to Show in Chart`}
        maxChartMetrics={maxChartMetrics}
        nonChartMetrics={nonChartMetrics}
        setChartMetrics={setCurrentChartMetrics}
        onRemoveMetric={removeMetric}
      />
    </ReportingDrawer>
  );
}

TimePeriodDrawer.propTypes = propTypes;

export default TimePeriodDrawer;
